import type { AuthState } from '@/contexts/AuthContext';
import { APP_ROUTES } from '@/constants/routes';

type MagicLinkSearch = {
  auth?: string;
  code?: string;
};

export type RouteAuthDecision =
  | { kind: 'redirect'; to: string; search?: MagicLinkSearch }
  | { kind: 'render' }
  | { kind: 'render-child' }
  | { kind: 'recovery' };

export function isAllowedAdminReturnUrl(value: string, sourceOrigin: string): boolean {
  try {
    const destination = new URL(value);
    if (destination.protocol !== 'https:') return false;

    const source = new URL(sourceOrigin);
    if (source.origin === 'https://app.til.sg') {
      return destination.origin === 'https://admin.til.sg';
    }

    const isLocalSource =
      source.protocol === 'https:' && source.hostname.endsWith('.til.test') && source.port === '';
    const isLocalAdminDestination =
      destination.port === '' &&
      (destination.hostname === 'admin.til.test' ||
        (destination.hostname.startsWith('admin-') && destination.hostname.endsWith('.til.test')));
    return isLocalSource && isLocalAdminDestination;
  } catch {
    return false;
  }
}

export function resolvePostLoginDestination(
  value: string | undefined,
  sourceOrigin: string,
  internalDestination: string
): { kind: 'internal'; to: string } | { kind: 'external'; url: string } {
  if (value && isAllowedAdminReturnUrl(value, sourceOrigin)) {
    return { kind: 'external', url: new URL(value).toString() };
  }
  return { kind: 'internal', to: internalDestination };
}

export function navigateToPostLoginDestination(
  destination: { kind: 'internal'; to: string } | { kind: 'external'; url: string },
  navigation: {
    assign: (url: string) => void;
    navigate: (to: string) => void;
  }
): void {
  if (destination.kind === 'external') {
    navigation.assign(destination.url);
    return;
  }
  navigation.navigate(destination.to);
}

function hasActiveMagicLink(search: MagicLinkSearch): boolean {
  return !!(search.auth && search.code);
}

export function resolveIndexRoute(
  authState: AuthState,
  search: MagicLinkSearch
): RouteAuthDecision {
  if (hasActiveMagicLink(search)) {
    return {
      kind: 'redirect',
      to: APP_ROUTES.LOGIN,
      search: { auth: search.auth, code: search.code },
    };
  }
  if (authState === 'authenticated') {
    return { kind: 'redirect', to: APP_ROUTES.HOME };
  }
  if (authState === 'unauthenticated') {
    return { kind: 'redirect', to: APP_ROUTES.LOGIN };
  }
  return { kind: 'recovery' };
}

export function resolveLoginRoute(
  authState: AuthState,
  search: MagicLinkSearch,
  pathname: string,
  authenticatedDestination: string
): RouteAuthDecision {
  if (pathname.startsWith(`${APP_ROUTES.LOGIN}/`)) {
    return { kind: 'render-child' };
  }
  if (hasActiveMagicLink(search)) {
    return { kind: 'render' };
  }
  if (authState === 'authenticated') {
    return { kind: 'redirect', to: authenticatedDestination };
  }
  if (authState === 'unavailable') {
    return { kind: 'recovery' };
  }
  return { kind: 'render' };
}

export function resolveAdminLoginRoute(
  isEnabled: boolean,
  getAuthState: () => AuthState
): RouteAuthDecision {
  if (!isEnabled) {
    return { kind: 'redirect', to: APP_ROUTES.LOGIN };
  }

  const authState = getAuthState();
  if (authState === 'authenticated') {
    return { kind: 'redirect', to: APP_ROUTES.HOME };
  }
  if (authState === 'unavailable') {
    return { kind: 'recovery' };
  }
  return { kind: 'render' };
}

export function shouldAttemptAdminAutoLogin(options: {
  showRecovery: boolean;
  isDevMode: boolean;
  hasEmail: boolean;
  hasPassword: boolean;
  alreadyAttempted: boolean;
}): boolean {
  return (
    !options.showRecovery &&
    options.isDevMode &&
    options.hasEmail &&
    options.hasPassword &&
    !options.alreadyAttempted
  );
}
